/**
 * IT Security self-assessment catalog (Preparation).
 * Weights are for client-side mock totals until backend owns scoring.
 * Never render weight values in the requestor UI.
 */

export type ItSecurityAnswers = Record<string, boolean | undefined>;

export type ItSecurityRiskBand = 'HIGH' | 'MEDIUM' | 'LOW';

export type ItSecurityQuestion = {
  code: string;
  number: number;
  text: string;
  /** Internal only — do not show in UI */
  weight: number;
  /**
   * Shown when the answer is Yes.
   * Replace placeholders with signed-off documentation action copy when available.
   */
  documentationAction: string;
};

export const IT_SECURITY_QUESTIONS: ItSecurityQuestion[] = [
  {
    code: 'ITS_Q1',
    number: 1,
    text: "If the system that we're testing is not correctly or securely set up, is there a risk that 'Restricted-Internal' or 'Confidential' data could be exposed or stolen?",
    weight: 50,
    documentationAction:
      'Required documentation: data classification and labelling for any Restricted-Internal or Confidential data in scope.',
  },
  {
    code: 'ITS_Q2',
    number: 2,
    text: "Could ISS' essential operations or processes be interrupted if the system or data we're testing becomes unavailable?",
    weight: 30,
    documentationAction:
      'Required documentation: disaster recovery / continuity considerations (DRP) for essential operations in scope.',
  },
  {
    code: 'ITS_Q3',
    number: 3,
    text: 'If the system that we\'re testing is not correctly or securely set up, could other important company systems, services or data be negatively impacted?',
    weight: 100,
    documentationAction:
      'Required documentation: technical / integration design covering impact on other ISS systems and services.',
  },
  {
    code: 'ITS_Q4',
    number: 4,
    text: "If the system or data we're testing isn't working, could this affect our ability to deliver products or services that we promised to our customers?",
    weight: 50,
    documentationAction:
      'Required documentation: service impact assessment and relevant SLAs for customer delivery commitments.',
  },
  {
    code: 'ITS_Q5',
    number: 5,
    text: "If the system that we're testing is not correctly or securely set up, could it result in our customer's data, systems, or network being exposed or inaccessible?",
    weight: 100,
    documentationAction:
      'Required documentation: customer data / systems protection measures and technical design for exposure risks.',
  },
  {
    code: 'ITS_Q6',
    number: 6,
    text: "Could ISS' reputation suffer if the system we're testing is compromised or stops working?",
    weight: 25,
    documentationAction:
      'Required documentation: reputation / communications considerations if the PoC system is compromised or unavailable.',
  },
  {
    code: 'ITS_Q7',
    number: 7,
    text: 'Could ISS face financial losses, including fraud or fines, if the system we\'re testing is compromised or stops working?',
    weight: 25,
    documentationAction:
      'Required documentation: financial / fraud / regulatory risk notes for the PoC scope.',
  },
];

export function isItSecurityComplete(answers: ItSecurityAnswers): boolean {
  return IT_SECURITY_QUESTIONS.every((q) => answers[q.code] !== undefined);
}

export function calculateItSecurity(answers: ItSecurityAnswers): {
  totalScore: number;
  riskBand: ItSecurityRiskBand;
  bandTitle: string;
  bandMessage: string;
  requiredDocumentationActions: { code: string; number: number; action: string }[];
} {
  let totalScore = 0;
  const requiredDocumentationActions: {
    code: string;
    number: number;
    action: string;
  }[] = [];

  for (const q of IT_SECURITY_QUESTIONS) {
    if (answers[q.code] === true) {
      totalScore += q.weight;
      requiredDocumentationActions.push({
        code: q.code,
        number: q.number,
        action: q.documentationAction,
      });
    }
  }

  let riskBand: ItSecurityRiskBand;
  let bandTitle: string;
  let bandMessage: string;

  // Confirmed: 50+ High, 1–49 Medium, 0 Low
  if (totalScore >= 50) {
    riskBand = 'HIGH';
    bandTitle = 'High risk';
    bandMessage =
      'You must set up a meeting with GICSS Cyber Risk (or the local GIS responsible). A notification is sent to them.';
  } else if (totalScore > 0) {
    riskBand = 'MEDIUM';
    bandTitle = 'Medium risk';
    bandMessage =
      'A notification is sent to GICSS Cyber Risk (or local GIS). GIS will review the ticket.';
  } else {
    riskBand = 'LOW';
    bandTitle = 'Low risk';
    bandMessage = 'Good to go.';
  }

  return {
    totalScore,
    riskBand,
    bandTitle,
    bandMessage,
    requiredDocumentationActions,
  };
}
